// src/components/adminAcquisition/AdminAcquisition.jsx
//
// /admin/acquisition (admins, linked from the commercial page): which landing
// page (?page=) and which Google Ads keyword bring owners who really use
// QRProofs - their « Afficher le QR » taps and their customers' verified
// ratings (backend routes/adminAcquisition.ts). /admin/landings covers the
// step before: visits, demo requests, sign-ups.
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Alert, CircularProgress, Chip, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import useRoleAuth from '../../utils/useRoleAuth';
import { acquisitionStats } from '../../utils/acquisitionApi';
import { PAGE_NAMES } from '../adminLandings/AdminLandings';

const pageName = (p) => (p ? PAGE_NAMES[p] || p : 'Sans page (Google Ads direct)');
const keyword = (k) => k || '—';
const perAccount = (n, accounts) => (accounts ? (n / accounts).toFixed(1).replace('.', ',') : '—');
const date = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

function Ranking({ title, rows, field, unit, testId }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }} data-testid={testId}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      {rows.length === 0 ? (
        <Typography color="text.secondary">Rien sur cette période.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell><TableCell>Mot-clé</TableCell><TableCell>Page</TableCell>
              <TableCell align="right">{unit}</TableCell><TableCell align="right">Comptes</TableCell>
              <TableCell align="right">Par compte</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={`${r.page}|${r.utm_term}|${r.utm_campaign}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell><b>{keyword(r.utm_term)}</b>{r.utm_campaign && <Typography variant="caption" display="block" color="text.secondary">{r.utm_campaign}</Typography>}</TableCell>
                <TableCell>{pageName(r.page)}</TableCell>
                <TableCell align="right"><b>{r[field]}</b></TableCell>
                <TableCell align="right">{r.accounts}</TableCell>
                <TableCell align="right">{perAccount(r[field], r.accounts)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export default function AdminAcquisition() {
  const { ready, forbidden, loading, getToken } = useRoleAuth(['ROLE_ADMIN']);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    // getToken() is synchronous (utils/useRoleAuth.js): the current token.
    acquisitionStats(days, getToken())
      .then((d) => { if (!cancelled) { setData(d); setError(''); } })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [ready, days, getToken]);

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Réservé aux administrateurs.</Alert></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Google Ads et pages d’entrée</Typography>
        <Box>
          <Button component={RouterLink} to="/admin/landings">Visites et inscriptions</Button>
          <Button component={RouterLink} to="/admin/commercial">Dashboard commercial</Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
        Les comptes créés après une arrivée par une page (?page=…) ou une annonce Google Ads, et ce qu’ils font ensuite :
        les QR codes affichés (« Afficher le QR ») et les vraies notes de leurs clients (vérifiées sur place), sur la période,
        pour tous leurs établissements. Les QR affichés sont comptés depuis le 25/10/2026 ; les établissements de test sont exclus.
      </Typography>

      <ToggleButtonGroup size="small" exclusive value={days} onChange={(_e, v) => v && setDays(v)} sx={{ mb: 2 }}>
        <ToggleButton value={7}>7 jours</ToggleButton>
        <ToggleButton value={30}>30 jours</ToggleButton>
        <ToggleButton value={90}>90 jours</ToggleButton>
      </ToggleButtonGroup>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Impossible de charger les chiffres ({error}).</Alert>}
      {!data && !error && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Ranking title="Mots-clés qui font afficher le plus de QR codes" rows={data.topByQrDisplays}
                field="qrDisplays" unit="QR affichés" testId="top-qr" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Ranking title="Mots-clés qui apportent le plus de vraies notes" rows={data.topByRatings}
                field="ratings" unit="Notes vérifiées" testId="top-ratings" />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Par page et mot-clé</Typography>
          <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
            <Table size="small" data-testid="by-keyword">
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell><TableCell>Mot-clé</TableCell><TableCell>Campagne</TableCell>
                  <TableCell align="right">Comptes créés (période)</TableCell><TableCell align="right">Comptes</TableCell>
                  <TableCell align="right">QR affichés</TableCell><TableCell align="right">QR / compte</TableCell>
                  <TableCell align="right">Notes vérifiées</TableCell><TableCell align="right">Notes / compte</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.keywords.length === 0 && (
                  <TableRow><TableCell colSpan={9}><i>Aucun compte venu d’une page ou de Google Ads pour l’instant.</i></TableCell></TableRow>
                )}
                {data.keywords.map((k) => (
                  <TableRow key={`${k.page}|${k.utm_term}|${k.utm_campaign}`}>
                    <TableCell>{pageName(k.page)}</TableCell>
                    <TableCell>{keyword(k.utm_term)}</TableCell>
                    <TableCell>{k.utm_campaign || '—'}</TableCell>
                    <TableCell align="right">{k.signups}</TableCell>
                    <TableCell align="right">{k.accounts}</TableCell>
                    <TableCell align="right">{k.qrDisplays}</TableCell>
                    <TableCell align="right">{perAccount(k.qrDisplays, k.accounts)}</TableCell>
                    <TableCell align="right">{k.ratings}</TableCell>
                    <TableCell align="right">{perAccount(k.ratings, k.accounts)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Comptes venus des pages et de Google Ads</Typography>
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" data-testid="accounts">
              <TableHead>
                <TableRow>
                  <TableCell>Inscription</TableCell><TableCell>Compte</TableCell><TableCell>Établissement</TableCell>
                  <TableCell>Page</TableCell><TableCell>Mot-clé</TableCell>
                  <TableCell align="right">QR affichés</TableCell><TableCell align="right">Notes vérifiées</TableCell>
                  <TableCell>Dernière note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.accounts.map((a) => (
                  <TableRow key={a.email} data-testid={`account-${a.email}`}>
                    <TableCell>{date(a.createdAt)}</TableCell>
                    <TableCell>
                      {a.email}
                      {a.googleAds && <Chip size="small" color="primary" variant="outlined" label="Google Ads" sx={{ ml: 1 }} />}
                      {a.qrDisplays === 0 && a.ratings === 0 && <Chip size="small" color="warning" label="inactif" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>{a.facilityName || '—'}</TableCell>
                    <TableCell>{pageName(a.page)}</TableCell>
                    <TableCell>{keyword(a.utm_term)}</TableCell>
                    <TableCell align="right">{a.qrDisplays}</TableCell>
                    <TableCell align="right">{a.ratings}</TableCell>
                    <TableCell>{date(a.lastRatingAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Container>
  );
}
